class ProductDescriptionFormatter {
  constructor(element) {
    this.element = element;
    // Add a class before formatting to prevent initial animation flash
    this.element.classList.add("is-formatting");
    this.formatDescription();
    // Remove the class after a brief delay to allow for proper initialization
    requestAnimationFrame(() => {
      this.element.classList.remove("is-formatting");
    });
  }

  formatDescription() {
    const content = this.element.innerHTML;
    const sections = this.parseIntoSections(content);
    this.element.innerHTML = this.renderSections(sections);
    this.setupEventListeners();
  }

  parseIntoSections(content) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    const sections = [];
    let currentSection = {
      title: "Description",
      content: [],
    };

    // Process each element
    doc.body.childNodes.forEach((node) => {
      if (node.nodeName === "P" && node.querySelector("strong")) {
        // If we have content in the current section, save it
        if (currentSection.content.length > 0) {
          sections.push({ ...currentSection });
        }
        // Start a new section
        currentSection = {
          title: node.textContent.trim(),
          content: [],
        };
      } else {
        // Add content to current section
        if (node.textContent.trim()) {
          currentSection.content.push(this.formatNode(node));
        }
      }
    });

    // Add the last section
    if (currentSection.content.length > 0) {
      sections.push(currentSection);
    }

    return sections;
  }

  formatNode(node) {
    if (node.nodeName === "UL") {
      return {
        type: "list",
        items: Array.from(node.querySelectorAll("li")).map((li) =>
          li.innerHTML.trim()
        ),
      };
    }
    return {
      type: "text",
      content: node.innerHTML.trim(),
    };
  }

  renderSections(sections) {
    return sections
      .map(
        (section, index) => `
      <div class="description-section">
        <button class="description-section__header" aria-expanded="false" aria-controls="section-${index}">
          <span class="description-section__title">${section.title}</span>
          <span class="description-section__icon">+</span>
        </button>
        <div class="description-section__content" id="section-${index}" hidden>
          ${this.renderSectionContent(section.content)}
        </div>
      </div>
    `
      )
      .join("");
  }

  renderSectionContent(content) {
    return content
      .map((item) => {
        if (item.type === "list") {
          return `
          <ul class="description-section__list">
            ${item.items.map((li) => `<li>${li}</li>`).join("")}
          </ul>
        `;
        }
        return `<p>${item.content}</p>`;
      })
      .join("");
  }

  setupEventListeners() {
    this.element
      .querySelectorAll(".description-section__header")
      .forEach((button) => {
        const content = button.nextElementSibling;

        // Initialize content to be hidden
        content.style.opacity = "0";
        content.style.maxHeight = "0";

        button.addEventListener("click", () => {
          const isExpanded = button.getAttribute("aria-expanded") === "true";
          const icon = button.querySelector(".description-section__icon");

          // Toggle aria-expanded
          button.setAttribute("aria-expanded", !isExpanded);

          // Update icon
          icon.textContent = isExpanded ? "+" : "-";

          // Handle content visibility
          if (isExpanded) {
            // Collapse
            content.addEventListener("transitionend", function handler(e) {
              if (e.propertyName === "opacity") {
                content.hidden = true;
                content.removeEventListener("transitionend", handler);
              }
            });
            content.style.opacity = "0";
            content.style.maxHeight = "0";
          } else {
            // Expand
            content.hidden = false;
            // Force a reflow to enable the transition
            content.offsetHeight;
            content.style.opacity = "1";
            content.style.maxHeight = content.scrollHeight + "px";
          }
        });
      });
  }
}

// Initialize on DOMContentLoaded
document.addEventListener("DOMContentLoaded", () => {
  const descriptionElements = document.querySelectorAll(
    "[data-formatted-description]"
  );
  descriptionElements.forEach(
    (element) => new ProductDescriptionFormatter(element)
  );
});
